import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { AlertCircle, RotateCw, ZoomIn, ZoomOut } from "lucide-react";
import { AppButton, IconButton, cn } from "../ui";
import Shimmer from "./Shimmer";
import { PDF_OPTIONS } from "../SelfPaced/pdfSource";

const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;
const A4_RATIO = 841.89 / 595.28;

/**
 * PdfView — a PDF the server rendered, drawn in the page.
 *
 * The fee invoice, the Settings → Invoices preview and the placement agreement
 * all show the PDF itself rather than an HTML copy of it, so what is on screen
 * is, by construction, what downloads, prints and is emailed. Pages are
 * paper-white in every theme — it is a document.
 *
 * Each `bytes` becomes one keyed document. A new one is drawn out of sight and
 * becomes the visible one when its first page has painted — its visibility
 * flips in place, so nothing remounts and a live preview never flashes back to
 * a placeholder.
 *
 * Props:
 *   bytes     Uint8Array of the PDF, or null while the first one loads
 *   label     accessible name ("Invoice INV-2026-0042")
 *   updating  a newer render is on its way (shown as a quiet note)
 *   error     a sentence; with `onRetry` when there is nothing to show yet
 */
export default function PdfView({ bytes, label = "Document", updating = false, error = "", onRetry, className }) {
  const boxRef = useRef(null);
  const seq = useRef(0);
  const shownRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [docs, setDocs] = useState([]); // [{ id, file, pages }], oldest first, at most the shown one + the newest
  const [shownId, setShownId] = useState(null);

  useEffect(() => { shownRef.current = shownId; }, [shownId]);

  useEffect(() => {
    if (!bytes) return;
    seq.current += 1;
    const id = seq.current;
    // pdf.js detaches the buffer it is handed, so each document gets its own copy.
    const doc = { id, file: { data: bytes.slice() }, pages: 0 };
    setDocs((ds) => [...ds.filter((d) => d.id === shownRef.current), doc]);
  }, [bytes]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === "undefined") {
      setWidth(el.clientWidth);
      return undefined;
    }
    const obs = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const painted = (id) => {
    if (id !== seq.current) return; // a newer render superseded this one
    setShownId(id);
    setDocs((ds) => ds.filter((d) => d.id === id));
  };

  const newest = docs[docs.length - 1];
  // Until the first render paints, the one loading is the one shown.
  const visibleId = shownId ?? newest?.id ?? null;
  const pageWidth = Math.max(240, Math.floor((width || 640) * zoom));
  const placeholder = <Shimmer height={Math.round((width || 640) * A4_RATIO)} />;

  return (
    <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-2", className)}>
      <div className="no-print tw-flex tw-min-h-8 tw-items-center tw-justify-end tw-gap-1" role="group" aria-label="Zoom">
        {updating && <span className="tw-mr-auto tw-text-xs tw-text-content-muted" aria-live="polite">Updating…</span>}
        <IconButton
          icon={ZoomOut}
          label="Zoom out"
          variant="ghost"
          size="sm"
          onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - 0.25).toFixed(2)))}
          disabled={zoom <= ZOOM_MIN}
        />
        <AppButton variant="ghost" size="xs" onClick={() => setZoom(1)} aria-label="Fit the page to the width" className="tw-tabular-nums">
          {Math.round(zoom * 100)}%
        </AppButton>
        <IconButton
          icon={ZoomIn}
          label="Zoom in"
          variant="ghost"
          size="sm"
          onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + 0.25).toFixed(2)))}
          disabled={zoom >= ZOOM_MAX}
        />
      </div>

      <div
        ref={boxRef}
        className="tw-relative tw-w-full tw-overflow-x-auto"
        role="document"
        aria-label={label}
        aria-busy={updating || shownId == null}
      >
        {error && shownId == null ? (
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4">
            <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-content">
              <AlertCircle size={16} className="tw-shrink-0 tw-text-danger" aria-hidden="true" />
              {error}
            </p>
            {onRetry && (
              <AppButton variant="outline" size="sm" onClick={onRetry}>
                <RotateCw size={14} aria-hidden="true" /> Try again
              </AppButton>
            )}
          </div>
        ) : (
          <>
            {!docs.length && placeholder}
            {docs.map((doc) => {
              const visible = doc.id === visibleId;
              return (
                <div
                  key={doc.id}
                  className={visible ? "" : "tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-opacity-0"}
                  aria-hidden={visible ? undefined : "true"}
                >
                  <Document
                    file={doc.file}
                    options={PDF_OPTIONS}
                    loading={visible ? placeholder : null}
                    error={null}
                    onLoadSuccess={({ numPages }) => setDocs((ds) => ds.map((d) => (d.id === doc.id ? { ...d, pages: numPages } : d)))}
                    className="tw-flex tw-flex-col tw-items-start tw-gap-4"
                  >
                    {Array.from({ length: doc.pages }, (_, i) => (
                      <div
                        key={i}
                        className="tw-overflow-hidden tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-[var(--paper)] tw-shadow-card"
                      >
                        <Page
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderAnnotationLayer={false}
                          renderTextLayer={visible}
                          loading={null}
                          onRenderSuccess={i === 0 ? () => painted(doc.id) : undefined}
                        />
                      </div>
                    ))}
                  </Document>
                </div>
              );
            })}
          </>
        )}
      </div>

      {error && shownId != null && (
        <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-danger-fg" role="alert">
          <AlertCircle size={14} aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}
